import React from 'react';
import {Typography} from '@mui/material';

/** Keys aligned with TaxiCabsWpEnum ROW names. */
export const ITL_TITLE_KEYS = [
    'TAXI_LICENSE_ROW',
    'FULL_PAY_TAXI_CAB_ROW',
    'HIRE_PURCHASE_TAXI_CAB_ROW',
] as const;

export type ItlTitleKey = (typeof ITL_TITLE_KEYS)[number];

/** Title enum key → WP payload array property (camelCase). */
export const ITL_TITLE_KEY_TO_ROW_KEY = {
    TAXI_LICENSE_ROW: 'taxiLicenseRow',
    FULL_PAY_TAXI_CAB_ROW: 'fullPayTaxiCabRow',
    HIRE_PURCHASE_TAXI_CAB_ROW: 'hirePurchaseTaxiCabRow',
} as const;

export type ItlSectionRowKey = (typeof ITL_TITLE_KEY_TO_ROW_KEY)[ItlTitleKey];

export const ITL_CATEGORY_ROW_KEYS = [
    ITL_TITLE_KEY_TO_ROW_KEY.TAXI_LICENSE_ROW,
    ITL_TITLE_KEY_TO_ROW_KEY.FULL_PAY_TAXI_CAB_ROW,
    ITL_TITLE_KEY_TO_ROW_KEY.HIRE_PURCHASE_TAXI_CAB_ROW,
] as const;

/** Default section / header titles (also used as switch labels when unchanged). */
export const DEFAULT_ITL_HEADER_TITLES: Record<ItlTitleKey, string> = {
    TAXI_LICENSE_ROW: 'Taxi Licensee',
    FULL_PAY_TAXI_CAB_ROW: '(Full Pay) Taxi Cabs',
    HIRE_PURCHASE_TAXI_CAB_ROW: '(Hire Purchase) Taxi Cabs',
};

export const DEFAULT_ITL_SWITCH_LABELS: Record<ItlTitleKey, string> = {
    ...DEFAULT_ITL_HEADER_TITLES,
};

export function normalizeItlTitle(value: string | null | undefined): string {
    return String(value ?? '').replace(/\r\n/g, '\n');
}

export function buildItlTitleBaseline(
    itlTitle?: Record<string, string> | null,
): Record<ItlTitleKey, string> {
    const baseline = {...DEFAULT_ITL_HEADER_TITLES};
    if (!itlTitle) {
        return baseline;
    }
    for (const key of ITL_TITLE_KEYS) {
        const raw = itlTitle[key];
        if (raw != null && String(raw).trim() !== '') {
            baseline[key] = normalizeItlTitle(raw);
        }
    }
    return baseline;
}

export function resolveItlDisplayTitles(
    itlTitle?: Record<string, string> | null,
): Record<ItlTitleKey, string> {
    return buildItlTitleBaseline(itlTitle);
}

export function getItlSwitchLabel(
    key: ItlTitleKey,
    displayTitle: string,
): string {
    const normalized = normalizeItlTitle(displayTitle);
    const defaultHeader = DEFAULT_ITL_HEADER_TITLES[key];
    if (normalized === defaultHeader || normalized.trim() === '') {
        return DEFAULT_ITL_SWITCH_LABELS[key];
    }
    return normalized;
}

export function renderItlHeaderContent(title: string): React.ReactNode {
    const lines = normalizeItlTitle(title).split('\n');
    const effective = lines.length > 0 ? lines : [''];
    return (
        <>
            {effective.map((line, index) => (
                <Typography key={index} align="left">
                    {line}
                </Typography>
            ))}
        </>
    );
}

/**
 * Diff draft titles against baseline.
 * - unchanged → omit from pending
 * - empty or equal to default header → pending '' (clear)
 * - otherwise → pending new value
 */
export function diffItlTitles(
    draft: Record<string, string>,
    baseline: Record<ItlTitleKey, string>,
): {pending: Record<string, string>; display: Record<ItlTitleKey, string>} {
    const pending: Record<string, string> = {};
    const display = {...DEFAULT_ITL_HEADER_TITLES};

    for (const key of ITL_TITLE_KEYS) {
        const edited = normalizeItlTitle(draft[key]);
        const base = normalizeItlTitle(baseline[key] ?? DEFAULT_ITL_HEADER_TITLES[key]);
        const defaultHeader = DEFAULT_ITL_HEADER_TITLES[key];

        if (edited === base) {
            display[key] = edited.trim() === '' ? defaultHeader : edited;
            continue;
        }

        if (edited.trim() === '' || edited === defaultHeader) {
            pending[key] = '';
            display[key] = defaultHeader;
        } else {
            pending[key] = edited;
            display[key] = edited;
        }
    }

    return {pending, display};
}
